import { Injectable } from '@angular/core';
import {
  ensureDialersDispositionListReady,
  getDialersDispositionList,
} from '../../../helper/dialersConfigs/dialers-disposition-list';
import { OzonetelSDKCoreService } from './ozonetel-sdk-core.service';

/**
 * Ozonetel SDK wrap-up (ACW) — disposition bridge between CallingComponent and DigitalConsole.
 *
 * SCOPE: Ozonetel SDK dialer only (callingType === 'ozonetel' && SDK mode). Cube dialer unchanged.
 *
 * END-TO-END FLOW
 * ───────────────
 * 1. Call ends → OzonetelSDKCoreService.handleCallEnded()
 *    - Saves call leg snapshot, sets wrapUpPending, emits DROP (does not submit disposition).
 *
 * 2. CallingComponent DROP handler → enterOzonetelWrapUp()
 *    - Keeps caller info on screen, Status = Disconnected, starts wrap timer.
 *
 * 3. loadDispositions() (this service)
 *    - Reads dialersDispositionList.json from S3 (helper/dialersConfigs file is reference-only).
 *    - Labels must match Ozonetel campaign disposition names for admin reporting.
 *    - Server reference: npa-server/.../OzonetelDispositionCodes.json
 *
 * 4. Agent selects disposition + optional comment → submitDisposition() (this service)
 *    → OzonetelSDKCoreService.submitWrapUpDisposition()
 *    → dc.setDisposition(disposition, comment, '', false, '')  // 4th arg false is required
 *
 * 5. On SDK success → completeWrapUpAfterDisposition() in core
 *    - wrapUpPending = false, re-arms agent, CallingComponent clears UI.
 *
 * 6. Wrap timer expiry → CallingComponent.autoDisposeCall() auto-submits first list item.
 *
 * GUARDS (while wrap-up active)
 * - SDK: onNewCall / onPerformCallEvent ignored; manual dial blocked.
 * - UI: endCall reconcile skipped; toggleAgentStatus / manual dial show error.
 */

/** One disposition option in the Ozonetel wrap-up dropdown. */
export interface OzonetelDispositionOption {
  id: string;
  reason: string;
  label: string;
}

@Injectable()
export class OzonetelWrapUpService {
  constructor(private ozonetelSDKCore: OzonetelSDKCoreService) {}

  /** Mirrors OzonetelSDKCoreService.wrapUpPending — true until disposition succeeds. */
  isWrapUpActive(): boolean {
    return this.ozonetelSDKCore.isWrapUpPending();
  }

  /**
   * Step 3 of wrap-up: build dropdown options from S3 dialersDispositionList.json.
   * Double underscores in JSON strings render as " / " in the UI label.
   */
  async loadDispositions(): Promise<OzonetelDispositionOption[]> {
    await ensureDialersDispositionListReady();
    const list = getDialersDispositionList();
    return list.map((reason) => ({
      id: reason,
      reason,
      label: reason.replace(/__/g, ' / '),
    }));
  }

  /**
   * Step 4 of wrap-up: validate selection and delegate to SDK core for dc.setDisposition().
   * Core completes wrap-up and re-arms agent only when SDK returns success.
   */
  async submitDisposition(
    reason: string,
    comment?: string
  ): Promise<{ success: boolean; message?: string }> {
    const disposition = String(reason || '').trim();
    if (!disposition) {
      return { success: false, message: 'Select a disposition' };
    }
    const result = await this.ozonetelSDKCore.submitWrapUpDisposition(disposition, comment?.trim() || '');
    return {
      success: result.success,
      message: result.message,
    };
  }
}
